import { useState, type KeyboardEvent, type SyntheticEvent } from 'react'
import { router, usePage } from '@inertiajs/react'
import { Building2, Search } from 'lucide-react'
import { t } from '@/shared/lib/i18n'
import { customerContextsPath } from '@/shared/lib/routes'
import { cn } from '@/shared/lib/utils'
import type { CustomerListItem } from '@/shared/types/CustomerListItem'
import type { SharedProps } from '@/shared/types/SharedProps'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ChevronDownIcon,
} from '@/shared/ui/dropdown-menu'
import { Input } from '@/shared/ui/input'

const filterCustomersByName = (
  customers: CustomerListItem[],
  query: string,
): CustomerListItem[] => {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) {
    return customers
  }

  return customers.filter((customer) =>
    customer.customerName.toLowerCase().includes(normalizedQuery),
  )
}

/** Block Radix menu typeahead while typing; still allow Escape to close. */
const stopMenuTypeahead = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    return
  }
  event.stopPropagation()
}

const stopDropdownDismissal = (event: SyntheticEvent) => {
  event.stopPropagation()
}

interface OrganizationSearchInputProps {
  value: string
  onChange: (query: string) => void
  inputClassName: string
}

const OrganizationSearchInput = ({
  value,
  onChange,
  inputClassName,
}: OrganizationSearchInputProps) => (
  <div className="relative">
    <Search
      className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
      aria-hidden
    />
    <Input
      type="search"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={t('customerSwitcher.searchPlaceholder')}
      aria-label={t('customerSwitcher.searchLabel')}
      autoComplete="off"
      className={cn('pl-9', inputClassName)}
    />
  </div>
)

const useCustomerContextSwitch = (currentCustomerId: number | null) => {
  const [processing, setProcessing] = useState(false)

  const submitSwitch = (customerId: number) => {
    if (currentCustomerId === null || customerId === currentCustomerId || processing) {
      return
    }

    setProcessing(true)
    router.post(
      customerContextsPath(),
      { customer_id: customerId },
      {
        preserveState: false,
        onFinish: () => setProcessing(false),
      },
    )
  }

  return { processing, submitSwitch }
}

const CustomerSwitcher = () => {
  const {
    props: { customerSwitcher },
  } = usePage<SharedProps>()
  const currentCustomerId = customerSwitcher?.currentCustomerId ?? null
  const { processing, submitSwitch } = useCustomerContextSwitch(currentCustomerId)
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  if (!customerSwitcher) {
    return null
  }

  const { currentCustomerName, customers } = customerSwitcher
  const filteredCustomers = filterCustomersByName(customers, searchQuery)

  const handleOpenChange = (open: boolean) => {
    setMenuOpen(open)
    if (!open) {
      setSearchQuery('')
    }
  }

  return (
    <DropdownMenu open={menuOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger
        type="button"
        disabled={processing}
        className="flex max-w-[14rem] cursor-pointer items-center gap-1 rounded-md border border-transparent px-2 py-1 text-left text-sm outline-none hover:bg-white/10 disabled:pointer-events-none disabled:opacity-50"
        aria-label={t('customerSwitcher.ariaOpen')}
        aria-haspopup="menu"
      >
        <Building2 className="size-4 shrink-0 text-white/70" strokeWidth={1.75} aria-hidden />
        <span className="min-w-0 flex-1 truncate font-medium text-white">
          {currentCustomerName}
        </span>
        <ChevronDownIcon className="size-3.5 shrink-0 text-white/70" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[12rem]">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          {t('customerSwitcher.switchOrganization')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div
          className="px-2 pb-2"
          onKeyDown={stopMenuTypeahead}
          onPointerDown={stopDropdownDismissal}
        >
          <OrganizationSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            inputClassName="h-8"
          />
        </div>
        {filteredCustomers.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-muted-foreground" role="status">
            {t('customerSwitcher.empty')}
          </p>
        ) : (
          filteredCustomers.map((customer) => {
            const isCurrent = customer.customerId === currentCustomerId
            return (
              <DropdownMenuItem
                key={customer.customerId}
                disabled={isCurrent || processing}
                onSelect={() => submitSwitch(customer.customerId)}
                className={isCurrent ? 'opacity-60' : undefined}
              >
                <span className="min-w-0 flex-1 truncate">{customer.customerName}</span>
                {isCurrent ? (
                  <span className="ml-2 shrink-0 text-xs text-muted-foreground">
                    {t('customerSwitcher.current')}
                  </span>
                ) : null}
              </DropdownMenuItem>
            )
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const CustomerSwitcherMobileList = () => {
  const {
    props: { customerSwitcher },
  } = usePage<SharedProps>()
  const currentCustomerId = customerSwitcher?.currentCustomerId ?? null
  const { processing, submitSwitch } = useCustomerContextSwitch(currentCustomerId)
  const [searchQuery, setSearchQuery] = useState('')

  if (!customerSwitcher) {
    return null
  }

  const { customers } = customerSwitcher
  const filteredCustomers = filterCustomersByName(customers, searchQuery)

  return (
    <div className="mt-4 border-t border-border/80 pt-3">
      <p className="px-1 pb-2 text-[0.7rem] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
        {t('customerSwitcher.switchOrganization')}
      </p>
      <div className="mb-2 px-1">
        <OrganizationSearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          inputClassName="h-9"
        />
      </div>
      <div className="space-y-0.5">
        {filteredCustomers.length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground" role="status">
            {t('customerSwitcher.empty')}
          </p>
        ) : (
          filteredCustomers.map((customer) => {
            const isCurrent = customer.customerId === currentCustomerId
            return (
              <button
                key={customer.customerId}
                type="button"
                disabled={isCurrent || processing}
                onClick={() => submitSwitch(customer.customerId)}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[15px] leading-snug transition-colors disabled:pointer-events-none disabled:opacity-50 sm:py-3"
              >
                <Building2
                  className="size-5 shrink-0 text-muted-foreground"
                  strokeWidth={isCurrent ? 2.25 : 1.75}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 leading-snug">{customer.customerName}</span>
                {isCurrent ? (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {t('customerSwitcher.current')}
                  </span>
                ) : null}
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}

export { CustomerSwitcher, CustomerSwitcherMobileList }
